import React from "react";
import { FormControl, FormControlLabel, FormLabel, RadioGroup } from "@mui/material";

import { RenderHTML } from "components";

import { ErrorMessage, RadioField, StyledRadio } from "../../shared/styled-components";
import { useMCQQuestionStyles } from "./style";
import { ErrorMessages } from "utils/constants";

interface MCQOption {
  optionId: string;
  htmlText: string;
  sequence: number;
  isSelected: boolean | null;
}

interface MCQQuestionProps {
  questionId: string;
  questionText: string;
  options: MCQOption[];
  isMandatory: boolean;
  showMandatoryError: boolean;
  onChange: (questionId: string, optionId: string, optionText: string) => void;
}

export const MCQQuestion: React.FC<MCQQuestionProps> = ({
  questionId,
  questionText,
  options,
  isMandatory,
  showMandatoryError,
  onChange,
}) => {
  const classes = useMCQQuestionStyles();
  const [optionId, setOptionId] = React.useState<string | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOptionArr = event.target.value.split("+");
    const optionId = selectedOptionArr[0];
    const optionText = selectedOptionArr[1];
    setOptionId(optionId);
    onChange(questionId, optionId, optionText);
  };

  const radioGroupId = `mcq-${questionId}`;
  const errorId = `${radioGroupId}-error`;

  return (
    <div className={classes.container}>
      <RadioField>
        <FormControl
          component="fieldset"
          aria-disabled="false"
          error={showMandatoryError && isMandatory}
        >
          <FormLabel
            component="legend"
            htmlFor={radioGroupId}
            className={classes.formLabel}
            aria-label={
              isMandatory ? `${questionText} ${ErrorMessages.InputRequired}` : questionText
            }
          >
            <div className={classes.questionText}>
              <RenderHTML content={questionText ?? ""} />
              {isMandatory && <span className={classes.requiredAsterisk}>*</span>}
            </div>
          </FormLabel>
          <RadioGroup
            id={radioGroupId}
            name={`question-${questionId}`}
            onChange={handleChange}
            row={false}
            className={showMandatoryError && isMandatory ? classes.radioGroupError : undefined}
            aria-describedby={showMandatoryError && isMandatory ? errorId : undefined}
          >
            {options?.map((option) => (
              <FormControlLabel
                key={option?.optionId}
                value={`${option?.optionId}+${option?.htmlText}`}
                title={option?.htmlText}
                aria-label={` ${option?.htmlText ?? ""}`}
                control={<StyledRadio />}
                label={
                  <div className={classes.optionLabel}>
                    <RenderHTML content={option?.htmlText ?? ""} />
                  </div>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
      </RadioField>
      {(isMandatory && !optionId) || (showMandatoryError && isMandatory) ? (
        <ErrorMessage id={errorId}>{`${questionText} ${ErrorMessages.InputRequired}`}</ErrorMessage>
      ) : null}
    </div>
  );
};
